import { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithCustomToken,
  signOut, 
  onAuthStateChanged,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth } from '../services/firebase';
import { functions } from '../services/firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [merchantData, setMerchantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sign in with merchant credentials using Cloud Function
  const login = async (merchantId, password) => {
    setError(null);
    try {
      console.log('[MDM] Attempting merchant login:', merchantId);
      
      // Call the merchantFLogin Cloud Function
      const merchantFLogin = httpsCallable(functions, 'merchantFLogin');
      const response = await merchantFLogin({ merchantId, password });
      
      console.log('[MDM] Cloud Function response received');
      
      const { token, merchant } = response.data;
      
      // Sign in to Firebase with the custom token
      const userCredential = await signInWithCustomToken(auth, token);
      
      console.log('[MDM] Firebase sign-in successful:', userCredential.user.uid);
      
      // Store merchant data
      setMerchantData(merchant);
      
      return { success: true, merchant };
    } catch (err) {
      console.error('[MDM] Login error:', err);
      
      let errorMessage = 'Failed to sign in. Please try again.';
      
      if (err.code === 'not-found') {
        errorMessage = 'Merchant ID not found';
      } else if (err.code === 'permission-denied') {
        errorMessage = 'Invalid merchant ID or password';
      } else if (err.message?.includes('network') || err.message?.includes('offline')) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Sign out
  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
      setMerchantData(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    merchantData,
    loading,
    error,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
